import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Stack,
  Button,
  Typography,
  Grid,
  Card,
  TextField,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  DialogActions,
  DialogContentText,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import ReactApexChart from 'react-apexcharts';
import { useSelector } from 'react-redux';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';
import HeatmapLegend from 'src/utils/mallManagement/HeatMapLegend';
import { SERVICES } from 'src/utils/servicePermissions';
import useServicePermission from 'src/hooks/useServicePermission';
import { toast } from 'react-toastify';
import moment from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

export default function MallManagementClickAndCollect() {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const customer = useSelector((state) => state.customerReducer);
  const mallID = customer?.customer?.id;
  const theme = useTheme();

  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [showApplyBanner, setShowApplyBanner] = useState(false);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);
  const [heatmapDataType, setHeatmapDataType] = useState('click-and-collect');
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);
  const [clickAndCollectList, setClickAndCollectList] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDate, setStartDate] = useState(moment().subtract(6, 'days'));
  const [endDate, setEndDate] = useState(moment());

  // Check service permission using hook
  const { hasPermission: clickAndCollectPermission, loading: permissionLoading } = useServicePermission(SERVICES.CLICK_AND_COLLECT);

  const handleOpenVideoModal = () => setVideoModalOpen(true);
  const handleCloseVideoModal = () => setVideoModalOpen(false);

  const handleApplyClick = () => setOpenApplyDialog(true);
  const handleCloseDialog = () => setOpenApplyDialog(false);
  const handleConfirmApply = async () => {
    try {
      const mallID = localStorage.getItem('mallID');

      const mallsResponse = await api.post('/malls/get-malls', { withOffices: true });

      if (mallsResponse.data && mallsResponse.data.status) {
        const foundMall = mallsResponse.data.result.find((item) => item.id === mallID);
        const mallTitle = foundMall?.title || 'Bilinmeyen AVM';

        const formData = {
          mallName: mallTitle,
          mallID: mallID,
          userName: parsedUser?.name || parsedUser?.email || 'Bilinmeyen Kullanıcı',
          serviceName: 'Click & Collect',
        };

        const response = await Services.createContactForm(parsedUser.token, formData);

        if (response.data && response.data.status) {
          toast.success('Talebiniz başarıyla iletilmiştir.');
          handleCloseDialog();
        } else {
          toast.error(response.data?.message || 'Talep gönderilirken bir hata oluştu.');
        }
      } else {
        toast.error('AVM bilgileri alınamadı.');
      }
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Talep gönderilirken bir hata oluştu.');
    }
  };

  useEffect(() => {    
    if (permissionLoading) {
      return;
    }
    
    if (!mallID) {
      setLoading(false);
      return;
    }

    const fetchAllData = async () => {
      setLoading(true);
      try {        
        const userHasPermission = clickAndCollectPermission;
        setHasPermission(userHasPermission);

        if (userHasPermission) {
          const response = await Services.GetLiveAnalyticsClickAndCollect(parsedUser.token, mallID);
          if (response.data && response.data.result) {
            setAnalyticsData(response.data.result);
          } else {
            setAnalyticsData({});
          }

          const listResponse = await Services.GetLiveAnalyticsClickAndCollectList(
            parsedUser.token,
            mallID,
            moment().subtract(6, 'days').format('YYYY-MM-DD'),
            moment().format('YYYY-MM-DD')
          );
          const list = listResponse?.data?.result || [];
          const mappedList = list.map((item, index) => ({
            id: item.id || `${item.barcode || 'row'}-${index}`,
            partner: item.api_user_type ? item.api_user_type.toUpperCase() : (item.title || '-'),
            barcode: item.barcode || '-',
            assignmentDate: item.in_branch_date_time ? moment.unix(Number(item.in_branch_date_time)).format('DD.MM.YYYY HH:mm') : '-',
            deliveryDate: item.completed_date_time ? moment.unix(Number(item.completed_date_time)).format('DD.MM.YYYY HH:mm') : '-',
            status: item.status_title || item.status || '-',
          }));
          setClickAndCollectList(mappedList);
        } else {
          setAnalyticsData({});
          setClickAndCollectList([]);
          setShowApplyBanner(true);
        }
      } catch (error) {
        console.error("Veri yüklenirken hata oluştu:", error);
        setAnalyticsData({});
        setClickAndCollectList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [mallID, parsedUser?.token, clickAndCollectPermission, permissionLoading]);

  useEffect(() => {
    setPage(1);
  }, [clickAndCollectList]);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const paginatedRows = clickAndCollectList.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleListByDate = async () => {
    if (!hasPermission) return;
    try {
      const response = await Services.GetLiveAnalyticsClickAndCollectList(
        parsedUser.token,
        mallID,
        moment(startDate).format('YYYY-MM-DD'),
        moment(endDate).format('YYYY-MM-DD')
      );
      const list = response?.data?.result || [];
      const mappedList = list.map((item, index) => ({
        id: item.id || `${item.barcode || 'row'}-${index}`,
        partner: item.api_user_type ? item.api_user_type.toUpperCase() : (item.title || '-'),
        barcode: item.barcode || '-',
        assignmentDate: item.in_branch_date_time ? moment.unix(Number(item.in_branch_date_time)).format('DD.MM.YYYY HH:mm') : '-',
        deliveryDate: item.completed_date_time ? moment.unix(Number(item.completed_date_time)).format('DD.MM.YYYY HH:mm') : '-',
        status: item.status_title || item.status || '-',
      }));
      setClickAndCollectList(mappedList);
    } catch (error) {
      console.error('Click & Collect liste sorgu hatası:', error);
      toast.error('Click & Collect listesi alınırken bir hata oluştu.');
    }
  };

  const handleExportExcel = () => {
    if (clickAndCollectList.length === 0) {
      toast.warn('Export edilecek veri bulunamadı.');
      return;
    }

    const exportData = clickAndCollectList.map((row) => ({
      'PARTNER': row.partner,
      'BARKOD': row.barcode,
      'ZİMMET TARİHİ': row.assignmentDate,
      'TESLİM TARİHİ': row.deliveryDate,
      'STATÜ': row.status,
    }));

    api.post('excel/export', {
      type: 'excel',
      content: exportData,
      header: {
        'PARTNER': 'string',
        'BARKOD': 'string',
        'ZİMMET TARİHİ': 'string',
        'TESLİM TARİHİ': 'string',
        'STATÜ': 'string',
      },
      sheetTitle: `Click_Collect_Listesi_${new Date().toISOString().slice(0, 10)}`
    })
      .then((response) => {
        if (response.data.status) {
          window.open(response.data.result, '_blank');
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        console.error('Excel export hatası:', error);
        toast.error(error.message || 'Excel dosyası oluşturulurken bir hata oluştu.');
      });
  };

  const zeroOutSeries = (series) => {
    if (!Array.isArray(series)) return [];
    const firstEl = series[0];
    if (typeof firstEl === 'number') {
      return series.map(() => 0);
    }
    if (typeof firstEl === 'object' && firstEl !== null && 'data' in firstEl) {
      return series.map(s => {
        if (Array.isArray(s.data) && typeof s.data[0] === 'object' && 'y' in s.data[0]) {
          return { ...s, data: s.data.map(item => ({ ...item, y: 0 })) };
        }
        if (Array.isArray(s.data)) {
          return { ...s, data: s.data.map(() => 0) };
        }
        return { ...s, data: [] };
      });
    }
    return [];
  };

  const DAY_MAP_TR = { monday: 'P.tesi', tuesday: 'Salı', wednesday: 'Çarş.', thursday: 'Perş.', friday: 'Cuma', saturday: 'C.tesi', sunday: 'Pazar' };
  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  const commonBarPlot = { bar: { columnWidth: '20%', borderRadius: 8, borderRadiusApplication: 'end' } };
  const deskBarPlot = { bar: { columnWidth: '40%', borderRadius: 8, borderRadiusApplication: 'end' } };
  const monthNow = new Date();
  const lastDayOfMonth = new Date(monthNow.getFullYear(), monthNow.getMonth() + 1, 0).getDate();
  const monthShortTr = new Intl.DateTimeFormat('tr-TR', { month: 'short' }).format(monthNow);
  const CLICK_KEY = 'click-and-collect';
  const RETURN_KEY = 'dropOff';

  const getNum = (obj, key) => Number(obj?.[key]) || 0;

  const clickBuckets = analyticsData?.totalCurrentMonthPostTotalCount?.[CLICK_KEY] || {};
  const returnBuckets = analyticsData?.totalCurrentMonthPostTotalCount?.[RETURN_KEY] || {};
  const labelRanges = ['1-7', '8-14', '15-21', `22-${lastDayOfMonth}`];

  const monthlyStackSeries = [
    { name: 'Teslimat', data: [getNum(clickBuckets, '1-7'), getNum(clickBuckets, '8-14'), getNum(clickBuckets, '15-21'), getNum(clickBuckets, '22-rest')] },
    { name: 'İade', data: [getNum(returnBuckets, '1-7'), getNum(returnBuckets, '8-14'), getNum(returnBuckets, '15-21'), getNum(returnBuckets, '22-rest')] },
  ];
  const monthlyStackOptions = {
    chart: { type: 'bar', stacked: true, toolbar: { show: false } },
    plotOptions: commonBarPlot,
    stroke: { show: true, width: 2, colors: ['#fff'] },
    colors: ['#1C64F2', '#FDBA8C'],
    xaxis: { categories: labelRanges.map(r => `${monthShortTr} ${r}`) },
    yaxis: { title: { text: 'Toplam Kullanım Adedi' } },
    legend: { position: 'bottom' },
    dataLabels: { enabled: false },
  };

  const toTitleCaseFromSlug = (slug = "") => slug.split("-").map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : "")).join(" ").trim();
  const generateColors = (n) => Array.from({ length: Math.max(1, n) }, (_, i) => `hsl(${Math.round((360 / Math.max(1, n)) * i)} 70% 50%)`);
  const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

  const monthlyBrandEntries = Object.entries(analyticsData?.totalCurrentMonthPostTotalBrandCount || {});
  const monthlyBrandLabels = monthlyBrandEntries.map(([k]) => {
    if (k === 'aras-kargo') {
      return "Aras'ın Pazar Yeri";
    }
    return toTitleCaseFromSlug(k);
  });
  const monthlyBrandSeries = monthlyBrandEntries.map(([, v]) => toNum(v));
  const monthlyBrandColors = generateColors(monthlyBrandSeries.length);
  const donut1Series = monthlyBrandSeries;
  const donut1Options = {
    chart: { type: 'donut', toolbar: { show: false } }, labels: monthlyBrandLabels, colors: monthlyBrandColors,
    plotOptions: { pie: { donut: { size: '65%', labels: { show: true, name: { show: true, fontSize: '15px', color: '#6B7280', fontWeight: '400', offsetY: 25 }, value: { show: true, fontSize: '30px', fontWeight: '700', offsetY: -12, color: '#111', formatter: (val) => `${val}` }, total: { show: true, label: 'Toplam', fontSize: '14px', color: '#999', formatter: (w) => `${w.globals.seriesTotals.reduce((a, b) => a + b, 0)}` } } } } },
    dataLabels: { enabled: false }, legend: { position: 'bottom', horizontalAlign: 'center' },
  };

  const weekDataClick = analyticsData?.totalWeeklyPostTotalCount?.[CLICK_KEY] || {};
  const weekDataReturn = analyticsData?.totalWeeklyPostTotalCount?.[RETURN_KEY] || {};

  const today = new Date(); 
  const dayOfWeek = today.getDay();

  const daysToSubtract = (dayOfWeek === 0) ? 6 : (dayOfWeek - 1);
  
  const mondayDt = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysToSubtract);

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(mondayDt.getFullYear(), mondayDt.getMonth(), mondayDt.getDate() + i);
    return day;
  });

  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  
  const monthShortFmt = new Intl.DateTimeFormat('tr-TR', { month: 'short' });
  const weeklyCategories = weekDates.map((d) => `${monthShortFmt.format(d)} ${String(d.getDate()).padStart(2, '0')}`);

  const weeklyStackSeries = [
    { name: 'Teslimat', data: weekDates.map((d) => Number(weekDataClick[toISO(d)]) || 0) },
    { name: 'İade', data: weekDates.map((d) => Number(weekDataReturn[toISO(d)]) || 0) },
  ];
  const weeklyStackOptions = {
    chart: { type: 'bar', stacked: true, toolbar: { show: false } }, plotOptions: deskBarPlot, yaxis: { title: { text: 'Toplam Kullanım Adedi' } }, stroke: { show: true, width: 2, colors: ['#fff'] },
    colors: ['#1C64F2', '#FDBA8C'], xaxis: { categories: weeklyCategories }, legend: { position: 'bottom' }, dataLabels: { enabled: false },
  };

  const weeklyBrandEntries = Object.entries(analyticsData?.totalWeeklyPostTotalBrandCount || {});
  const weeklyBrandLabels = weeklyBrandEntries.map(([k]) => {
    if (k === 'aras-kargo') {
      return "Aras'ın Pazar Yeri";
    }
    return toTitleCaseFromSlug(k);
  });
  const weeklyBrandSeries = weeklyBrandEntries.map(([, v]) => toNum(v));
  const weeklyBrandColors = generateColors(weeklyBrandSeries.length);
  const loyaltyData = analyticsData?.totalWeeklyCustomerLoyaltyCount || {};
  const oldCustomerCount = loyaltyData.oldCustomer || 0;
  const newCustomerCount = loyaltyData.newCustomers || 0;

  const donut2Series = [oldCustomerCount, newCustomerCount];
  const donut2Labels = ['Düzenli Müşteri', 'Yeni Müşteri'];
  const donut2Colors = ['#1C64F2', '#16BDCA'];

  const donut2Options = {
    chart: { type: 'donut', toolbar: { show: false } },
    labels: donut2Labels,
    colors: donut2Colors,
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            name: { show: true, fontSize: '15px', color: '#6B7280', fontWeight: '400', offsetY: 25 },
            value: { show: true, fontSize: '30px', fontWeight: '700', offsetY: -12, color: '#111', formatter: (val) => `${val}` },
            total: {
              show: true,
              label: 'Toplam',
              fontSize: '14px',
              color: '#999',
              formatter: (w) => `${w.globals.seriesTotals.reduce((a, b) => a + b, 0)}`
            }
          }
        }
      }
    },
    dataLabels: { enabled: false },
    legend: { position: 'bottom', horizontalAlign: 'center' },
  };

  // --- Yıllık Kullanım Verisi ve Grafiği (Son 12 Ay) ---
  const yearlyUsageData = analyticsData?.yearlyUsageTotalCount || {};
  const yearlyClickObj = yearlyUsageData[CLICK_KEY] || {};
  const yearlyReturnObj = yearlyUsageData[RETURN_KEY] || {};

  // Benzersiz ay key'lerini toplayıp kronolojik olarak sıralıyoruz
  const allYearlyMonths = Array.from(new Set([
    ...Object.keys(yearlyClickObj),
    ...Object.keys(yearlyReturnObj)
  ])).sort((a, b) => a.localeCompare(b));

  // "2025-12" formatını "Ara 2025" gibi bir formata dönüştürüyoruz
  const yearlyCategories = allYearlyMonths.map(monthStr => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1, 1);
    const mFormat = new Intl.DateTimeFormat('tr-TR', { month: 'short' }).format(date);
    const mTitle = mFormat.charAt(0).toUpperCase() + mFormat.slice(1);
    return `${mTitle} ${year}`;
  });

  const yearlyClickSeriesData = allYearlyMonths.map(m => Number(yearlyClickObj[m]) || 0);
  const yearlyReturnSeriesData = allYearlyMonths.map(m => Number(yearlyReturnObj[m]) || 0);

  const yearlyUsageSeries = [
    { name: 'Teslimat', type: 'bar', data: yearlyClickSeriesData },
    { name: 'İade', type: 'bar', data: yearlyReturnSeriesData }
  ];

  const yearlyUsageOptions = {
    chart: { toolbar: { show: false } },
    stroke: { width: 3, curve: 'smooth' },
    colors: ['#1C64F2', '#FDBA8C'], // Diğer grafiklerdeki Teslimat/İade renkleriyle uyumlu
    xaxis: {
      categories: yearlyCategories,
      labels: {
        rotate: -45,
        rotateAlways: false,
      }
    },
    yaxis: {
      title: { text: 'Kullanım Adedi' },
      labels: { formatter: (val) => val.toFixed(0) }
    },
    legend: { position: 'bottom' },
    dataLabels: { enabled: false },
    tooltip: {
      y: { formatter: (val) => `${val} adet` }
    }
  };
  // --------------------------------------------------------

  const dailyHeatmapChart = useMemo(() => {
    const rawData = analyticsData?.dayHourlyUsageTotalCount ?? {};
    const data = rawData[heatmapDataType] || {};
    const allTimeSlots = Array.from({ length: 16 }, (_, i) => { const startHour = 8 + i, endHour = startHour + 1; const formatHour = (h) => h.toString().padStart(2, '0'); const endHourFormatted = endHour === 24 ? '00' : formatHour(endHour); return `${formatHour(startHour)}:00-${endHourFormatted}:00`; });

    // Her saat için satır toplamlarını hesapla
    const hourlyTotals = allTimeSlots.map(timeSlot => {
      return dayOrder.reduce((sum, dayKey) => {
        const apiDayKey = dayKey.charAt(0).toUpperCase() + dayKey.slice(1);
        return sum + (data[apiDayKey]?.[timeSlot] || 0);
      }, 0);
    });

    // Her gün için sütun toplamlarını hesapla
    const dailyTotals = dayOrder.map(dayKey => {
      const apiDayKey = dayKey.charAt(0).toUpperCase() + dayKey.slice(1);
      return allTimeSlots.reduce((sum, timeSlot) => sum + (data[apiDayKey]?.[timeSlot] || 0), 0);
    });

    // Genel toplam
    const grandTotal = dailyTotals.reduce((sum, val) => sum + val, 0);

    // En alta günlük toplamlar satırı ekle
    const totalRow = {
      name: 'Toplam',
      data: [
        ...dayOrder.map((dayKey, index) => ({ x: DAY_MAP_TR[dayKey], y: dailyTotals[index] })),
        { x: 'Toplam', y: grandTotal }
      ]
    };

    // Series oluştur - her satıra "Toplam" sütunu ekle
    const series = [
      totalRow,
      ...allTimeSlots.map((timeSlot, index) => ({
        name: timeSlot.split('-')[0],
        data: [
          ...dayOrder.map(dayKey => {
            const apiDayKey = dayKey.charAt(0).toUpperCase() + dayKey.slice(1);
            return { x: DAY_MAP_TR[dayKey], y: data[apiDayKey]?.[timeSlot] || 0 };
          }),
          { x: 'Toplam', y: hourlyTotals[index] }
        ]
      })).reverse()
    ];

    const allCounts = Object.values(data).flatMap(day => (day && typeof day === 'object') ? Object.values(day) : []);
    const maxCount = allCounts.length > 0 ? Math.max(...allCounts, 0) : 10;
    const clickColors = ['#D7D7D7', '#93D4F4', '#46A5F9', '#4086F5', '#345CC3'];
    const dropOffColors = ['#D7D7D7', '#89D6CE', '#54C4B8', '#14B8A6', '#188B7E'];
    const activeColors = heatmapDataType === 'click-and-collect' ? clickColors : dropOffColors;
    const options = {
      chart: { type: 'heatmap', toolbar: { show: false } },
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.5,
          radius: 2,
          colorScale: {
            ranges: [
              { from: 0, to: 0, color: activeColors[0] },
              { from: 1, to: Math.floor(maxCount * 0.25), color: activeColors[2] },
              { from: Math.floor(maxCount * 0.25) + 1, to: Math.floor(maxCount * 0.7), color: activeColors[3] },
              { from: Math.floor(maxCount * 0.7) + 1, to: maxCount, color: activeColors[4] }
            ]
          }
        }
      },
      dataLabels: {
        enabled: true,
        style: {
          colors: ['#000'],
          fontSize: '12px',
          fontWeight: 500
        }
      },
      stroke: { show: true, width: 3, colors: ['#fff'] },
      xaxis: { type: 'category' },
      legend: { show: false },
      tooltip: { y: { formatter: (value) => `${value} adet` } },
    };
    return { series, options, maxCount, colors: activeColors };
  }, [analyticsData, heatmapDataType]);


  if (loading || permissionLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ px: 4, py: 3 }}>
      <Helmet><title>Click & Collect</title></Helmet>

      {showApplyBanner && !hasPermission && (
        <Stack sx={{ width: '100%', mb: 4, mx: 'auto', borderBottom: '1px solid #D6BBFB', borderRadius: 1, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
          <Alert
            severity="info"
            sx={{
              backgroundColor: 'white',
              alignItems: 'center',
              pr: 4,
              '& .MuiAlert-icon': {
                color: theme.palette.info.main
              }
            }}
            action={
              <Stack direction="row" gap={1} alignItems="center">
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={handleApplyClick}
                  sx={{ padding: '10px 16px', cursor: 'pointer' }}
                >
                  Başvur
                </Button>
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setShowApplyBanner(false);
                  }}
                >
                  <Iconify icon="eva:close-fill" width={20} height={20} />
                </IconButton>
              </Stack>
            }
            onClose={() => setShowApplyBanner(false)}
          >
            Bu hizmet şu anda aktif değil. Grafik ve liste verilerini görüntülemek için başvuruda bulunabilirsiniz.
          </Alert>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Click & Collect</Typography>
        <Button variant="text" startIcon={<Iconify icon="eva:play-circle-fill" />} sx={{ textTransform: 'none' }} onClick={handleOpenVideoModal}>
          Tanıtım Videosunu İzle
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Card elevation={0} sx={{ display: 'flex', alignItems: 'center', p: 2, minHeight: 140, flex: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">Günlük Kullanım Adedi (Bugün)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Toplam: <strong>{analyticsData?.totalDailyPostUsage ?? 0}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ortalama: <strong>{analyticsData?.totalCurrentMonthAvaragePostUsage?.toFixed(0) ?? '0.00'}</strong>
              </Typography>
            </Box>
            <Box sx={{ width: 40, height: 40, borderRadius: 1, background: 'linear-gradient(135deg,#3461D6 0%,#264697 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Iconify icon='eva:layers-outline' width={20} height={20} sx={{ color: '#fff' }} />
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ position: 'relative', p: 2, bgcolor: 'background.paper', borderRadius: 2, height: 360 }}>
            <Typography variant="h6" gutterBottom>Haftalık Click & Collect Hizmeti Kullanımı</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Kullanım Adedi</Typography>
            <ReactApexChart options={monthlyStackOptions} series={hasPermission ? monthlyStackSeries : zeroOutSeries(monthlyStackSeries)} type="bar" height={260} />
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ position: 'relative', p: 2, bgcolor: 'background.paper', borderRadius: 2, height: 360 }}>
            <Typography variant="h6" gutterBottom>Aylık Click & Collect Hizmeti Kullanımı</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Firmalara Göre Dağılımı</Typography>
            <ReactApexChart options={donut1Options} series={hasPermission ? donut1Series : zeroOutSeries(donut1Series)} type="donut" height={260} />
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ position: 'relative', p: 2, bgcolor: 'background.paper', borderRadius: 2, height: 360 }}>
            <Typography variant="h6" gutterBottom>Günlük Click & Collect Hizmeti Kullanımı</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Kullanım Adedi</Typography>
            <ReactApexChart options={weeklyStackOptions} series={hasPermission ? weeklyStackSeries : zeroOutSeries(weeklyStackSeries)} type="bar" height={260} />
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ position: 'relative', p: 2, bgcolor: 'background.paper', borderRadius: 2, height: 360 }}>
            <Typography variant="h6" gutterBottom>Haftalık Click & Collect Hizmeti Kullanımı</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Müşteri Segmentasyonuna Göre Dağılımı</Typography>
            <ReactApexChart options={donut2Options} series={hasPermission ? donut2Series : zeroOutSeries(donut2Series)} type="donut" height={260} />
          </Card>
        </Grid>

        {/* EKLENEN 12 AYLIK GRAFİK ALANI */}
        <Grid item xs={12}>
          <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, height: 360 }}>
            <Typography variant="h6" gutterBottom>Aylık Kullanım Hareketleri (Son 12 Ay)</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Teslimat ve İade Adedi</Typography>
            <ReactApexChart 
              options={yearlyUsageOptions} 
              series={hasPermission ? yearlyUsageSeries : zeroOutSeries(yearlyUsageSeries)} 
              type="bar" 
              height={280} 
            />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Box>
                <Typography variant="h6">Gün ve Saat Bazlı Kullanım Hareketleri (Haftalık)</Typography>
                <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="body2">Saat Aralığı</Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Veri Tipi</InputLabel>
                <Select value={heatmapDataType} label="Veri Tipi" onChange={(e) => setHeatmapDataType(e.target.value)}>
                  <MenuItem value="click-and-collect">Teslimat</MenuItem>
                  <MenuItem value="dropOff">İade</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <ReactApexChart options={dailyHeatmapChart.options} series={hasPermission ? dailyHeatmapChart.series : zeroOutSeries(dailyHeatmapChart.series)} type="heatmap" height={320} />
              </Box>
              <HeatmapLegend maxCount={dailyHeatmapChart.maxCount} colors={dailyHeatmapChart.colors} label={heatmapDataType === 'click-and-collect' ? 'Teslimat' : 'İade'} />
            </Stack>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper', borderRadius: 2 }} elevation={0}>
        <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={3}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                renderInput={(params) => <TextField {...params} fullWidth size="small" placeholder="Başlangıç Tarihi" />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={3}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                renderInput={(params) => <TextField {...params} fullWidth size="small" placeholder="Bitiş Tarihi" />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button fullWidth variant="contained" onClick={handleListByDate}>LİSTELE</Button>
          </Grid>
          <Grid item xs />
          <Grid item xs={12} sm={2}>
            <Button fullWidth variant="outlined" onClick={handleExportExcel} disabled={clickAndCollectList.length === 0}>EXCEL'E AKTAR</Button>
          </Grid>
        </Grid>
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: 'background.paper' }}>
              <TableRow>
                {['PARTNER', 'BARKOD', 'ZİMMET TARİHİ', 'TESLİM TARİHİ', 'STATÜ'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: 12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.partner}</TableCell>
                  <TableCell>{row.barcode}</TableCell>
                  <TableCell>{row.assignmentDate}</TableCell>
                  <TableCell>{row.deliveryDate}</TableCell>
                  <TableCell>{row.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={5} sx={{ py: 2 }}>
                  <Grid container alignItems="center">
                    <Grid item xs><Typography variant="caption">Toplam {clickAndCollectList.length} kayıt</Typography></Grid>
                    <Grid item sx={{ mr: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 90 }}>
                        <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                          {[10, 25, 50, 100].map((size) => (
                            <MenuItem key={size} value={size}>{size}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item>
                      <Pagination count={Math.ceil(clickAndCollectList.length / rowsPerPage)} page={page} onChange={(e, val) => setPage(val)} shape="rounded" size="small" />
                    </Grid>
                  </Grid>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={isVideoModalOpen} onClose={handleCloseVideoModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Tanıtım Videosu
          <IconButton aria-label="close" onClick={handleCloseVideoModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <video width="100%" height="auto" controls autoPlay src="https://files.easypoint.com.tr/files/data-ekranlari/click-collect.mp4">
            Tarayıcınız video etiketini desteklemiyor.
          </video>
        </DialogContent>
      </Dialog>

      <Dialog open={openApplyDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hizmet Başvurusu</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bu hizmetle ilgili bilgi alma isteğiniz Genel Merkezimize iletilecektir. Onaylıyor musunuz?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleConfirmApply} autoFocus>Onayla</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
